import { CountryCode } from '@scale/shared/types/demographics';
import {
  WorkerExperienceQualificationThresholds,
  ProjectPlatform,
  WorkerSource,
  TaskStatus,
} from '@scale/shared/defaults';
import {
  GenAIProjectType,
  GenAIProjectMedium,
  GenAIProjectUseCase,
} from '@scale/shared/types/projectParameterGroup';
import {
  ReviewLevel,
  ReviewLevelOrder,
  WorkerSkillEntryStatus,
  WorkerSkillDimensionQualityCategory,
  WorkerSkillDimensionQualityCategoryOrder,
} from '@scale/shared-types';
import { TaskRollupHumanNodeStatus } from '@scale/shared/pipelinev3/humanNodeStatus';
import { ColumnOptionMap, SelectOption } from '@scale/compass-ui/components/filters/Filters';

export function getInputFilterOptionsForColumnId(columnId: string): SelectOption[] {
  const id = columnId.toLowerCase().replace(/ /g, '');

  if (id in COLUMN_ID_TO_OPTIONS) {
    return COLUMN_ID_TO_OPTIONS[id] ?? [];
  }

  return [];
}

const COUNTRY_CODE_OPTIONS: SelectOption[] = Object.values(CountryCode).map((c: CountryCode) => {
  return {
    value: c,
    label: c,
  };
});

const WORKER_SOURCE_OPTIONS: SelectOption[] = Object.values(WorkerSource).map(
  (ws: (typeof WorkerSource)[keyof typeof WorkerSource]) => {
    return {
      value: ws,
      label: ws,
    };
  },
);

const EXPERIENCE_PLATFORM_OPTIONS: SelectOption[] = Object.values(ProjectPlatform).map(
  (p: ProjectPlatform) => {
    return {
      value: p,
      label: p,
    };
  },
);

const EXPERIENCE_PROJECT_ATTRIBUTES_OPTIONS: SelectOption[] = [
  ...Object.values(GenAIProjectType),
  ...Object.values(GenAIProjectMedium),
  ...Object.values(GenAIProjectUseCase),
].map((attr: GenAIProjectType | GenAIProjectMedium | GenAIProjectUseCase) => {
  return {
    value: attr,
    label: attr,
  };
});

const EXPERIENCE_MIN_TASKS_OPTIONS: SelectOption[] = Object.values(
  WorkerExperienceQualificationThresholds,
)
  .filter(t => typeof t === 'number')
  .map((t: WorkerExperienceQualificationThresholds | string) => {
    return {
      value: t.toString(),
      label: t.toString(),
    };
  });

const REVIEW_LEVEL_OPTIONS: SelectOption[] = ReviewLevelOrder.filter(
  t => typeof t === 'number',
).map((reviewLevel: ReviewLevel) => {
  return {
    value: reviewLevel.toString(),
    label: `L${reviewLevel.toString()}`,
  };
});

const HUMAN_NODE_STATUS_OPTIONS: SelectOption[] = Object.values(TaskRollupHumanNodeStatus)
  .filter((value, index, self) => self.indexOf(value) === index)
  .map((status: string | TaskRollupHumanNodeStatus.Pending | TaskRollupHumanNodeStatus.Paused) => {
    return {
      value: status.toString(),
      label: status.toString(),
    };
  });

const TASK_STATUS_OPTIONS: SelectOption[] = Object.values(TaskStatus).map((status: TaskStatus) => {
  return {
    value: status,
    label: status,
  };
});

const WORKER_SKILL_STATUS_OPTIONS: SelectOption[] = Object.values(WorkerSkillEntryStatus).map(
  (level: WorkerSkillEntryStatus) => {
    return {
      value: level,
      label: level,
    };
  },
);

const WORKER_SKILL_TIER_OPTIONS: SelectOption[] = WorkerSkillDimensionQualityCategoryOrder.map(
  (tier: WorkerSkillDimensionQualityCategory, index: number) => {
    return {
      value: index.toString(),
      label: tier,
    };
  },
);

export const COLUMN_ID_TO_OPTIONS: ColumnOptionMap = {
  countrycode: COUNTRY_CODE_OPTIONS,
  workersource: WORKER_SOURCE_OPTIONS,
  experienceplatform: EXPERIENCE_PLATFORM_OPTIONS,
  experienceprojectattribute: EXPERIENCE_PROJECT_ATTRIBUTES_OPTIONS,
  experiencemintasks: EXPERIENCE_MIN_TASKS_OPTIONS,
  reviewlevel: REVIEW_LEVEL_OPTIONS,
  humannodestatus: HUMAN_NODE_STATUS_OPTIONS,
  taskstatus: TASK_STATUS_OPTIONS,
  'workerSkill.status': WORKER_SKILL_STATUS_OPTIONS,
  'workerSkill.tier': WORKER_SKILL_TIER_OPTIONS,
};
